import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { Icon } from "../components/Icon.tsx";
import { StatePanel } from "../components/StatePanel.tsx";
import ui from "../components/ui.module.css";
import { Alerts } from "../components/usage/Alerts.tsx";
import { CostTable } from "../components/usage/CostTable.tsx";
import { DailyRequests } from "../components/usage/DailyRequests.tsx";
import { HistoryTable } from "../components/usage/HistoryTable.tsx";
import { MonthPanel } from "../components/usage/MonthPanel.tsx";
import { TopFiles } from "../components/usage/TopFiles.tsx";
import styles from "../components/usage/Usage.module.css";
import { WorkersCliff } from "../components/usage/WorkersCliff.tsx";
import { formatMonth, formatUsd } from "../lib/format.ts";
import { messageOf, usageQuery } from "../lib/queries.ts";

export const Route = createFileRoute("/usage")({
  loader: ({ context }) => context.queryClient.ensureQueryData(usageQuery),
  component: UsagePage,
  pendingComponent: UsagePending,
  errorComponent: UsageError,
});

function UsagePage() {
  const { data: usage } = useSuspenseQuery(usageQuery);

  useEffect(() => {
    document.title = "Usage · Vault";
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <div className={styles.heading}>
          <h1 className={styles.title}>Usage</h1>
          <dl className={styles.stats}>
            <div>
              <dt>Month</dt>
              <dd>{formatMonth(usage.month.start)}</dd>
            </div>
            <div>
              <dt>Plan</dt>
              <dd>{usage.plan === "paid" ? "Workers Paid" : "Free"}</dd>
            </div>
            <div>
              <dt>On track for</dt>
              <dd>{formatUsd(usage.costs.projectedUsd)}</dd>
            </div>
          </dl>
        </div>
      </div>

      {usage.alerts.length > 0 ? <Alerts alerts={usage.alerts} /> : null}

      <WorkersCliff usage={usage} />
      <MonthPanel usage={usage} />
      <CostTable costs={usage.costs} />
      <DailyRequests usage={usage} />
      <TopFiles topFiles={usage.topFiles} />
      <HistoryTable history={usage.history} />
    </div>
  );
}

function UsagePending() {
  return (
    <div className={styles.page} aria-busy="true">
      <div className={styles.intro}>
        <div className={styles.heading}>
          <h1 className={styles.title}>Usage</h1>
          <span className={`${ui.skeleton} ${styles.statsSkeleton}`} />
        </div>
      </div>
      <div className={`${ui.card} ${styles.pendingCliff}`}>
        <span className={`${ui.skeleton} ${styles.pendingMark}`} />
        <span className={`${ui.skeleton} ${styles.pendingTitle}`} />
        <span className={`${ui.skeleton} ${styles.pendingBar}`} />
      </div>
      <div className={`${ui.card} ${styles.panel}`}>
        <span className={`${ui.skeleton} ${styles.pendingTitle}`} />
        <div className={styles.meters}>
          {["storage", "class-a", "class-b", "requests"].map((row) => (
            <div key={row} className={styles.pendingMeter}>
              <span className={`${ui.skeleton} ${styles.pendingLine}`} />
              <span className={`${ui.skeleton} ${styles.pendingBar}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UsageError({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  return (
    <StatePanel icon="alert" title="Usage did not load" detail={messageOf(error)}>
      <button
        type="button"
        className={ui.button}
        onClick={() => {
          reset();
          void router.invalidate();
        }}
      >
        <Icon name="retry" />
        Try again
      </button>
    </StatePanel>
  );
}
